import React, { useEffect, useState, useCallback } from 'react';
import ButtonAuth from '../../../components/ButtonAuth';
import Input from '../../../components/Input';
import rocketBoyImg from '../../../assets/images/barbfullLogo.png';
import { Controller, useForm } from 'react-hook-form';
import Alert from '../../../components/Alert';
import { useTrackingContext } from '../../../context/trackingContext';
import config from '../../../config';
import useClient from '../../../hooks/useClient';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const ForgotPassword = () => {
  const { trackPageView, trackFormStart, trackFormSubmit, trackError } = useTrackingContext();

  useEffect(() => {
    trackPageView({
      pageName: 'Forgot Password page'
    });
  }, []);

  const [showAlert, setShowAlert] = useState(false);
  const client = useClient();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isDirty }
  } = useForm({ mode: 'onTouched' });

  useEffect(() => {
    isDirty &&
      trackFormStart({
        formName: 'Forgot password form start'
      });
  }, [isDirty]);

  const onSubmit = async (data) => {
    trackFormSubmit({
      formName: 'Forgot password form submit'
    });

    const captchaToken = await handleReCaptchaVerify();

    try {
      await client.post(`${config.everywhereApi}/users/self/recovery/forgot-password`, {
        emailAddress: data.email,
        captchaToken
      });
      setShowAlert({
        text: data.email,
        success: true
      });
    } catch (err) {
      const error = err?.response?.data?.error;

      setShowAlert({
        text: error?.errorMessage,
        success: false
      });

      trackError({ error_text: error?.errorMessage });
    }
  };

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    return executeRecaptcha('forgotpassword');
  }, [executeRecaptcha]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h1 className="font-Korolev-Bold text-5xl xl:text-5xl uppercase mb-6">
          forgot <br /> password
        </h1>

        <img src={rocketBoyImg} alt="build rocket boy" className="w-full mb-5" />

        <p className="text-base text-black mb-5">
          Enter the email address on your Account to have a password reset email sent to you.{' '}
        </p>

        <div className="mb-5">
          <Controller
            control={control}
            defaultValue=""
            name="email"
            rules={{
              required: {
                value: true,
                message: 'Email is required'
              },
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Please enter a valid email address'
              }
            }}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
              <Input
                type="email"
                placeholder="Email Address"
                value={value}
                autoFocus
                onChange={onChange}
                onBlur={onBlur}
                className="w-full"
                questionIcon={false}
                error={error}
              />
            )}
          />
        </div>

        <ButtonAuth
          label={'submit'}
          className="mb-3 lg:mb-5"
          type={'submit'}
          isDisabled={!isValid}
          isSubmitting={isSubmitting}
        />

        {showAlert && (
          <Alert setShowAlert={setShowAlert} success={showAlert.success}>
            {showAlert.success ? (
              <p className="pr-12 text-sm md:text-base">
                If your email exists in our database, a password reset link has been sent to{' '}
                {showAlert.text}, <br /> and will be valid for the next 20 minutes.
              </p>
            ) : (
              <p className="pr-12 text-sm md:text-base">
                Unable to Request Password Reset: {showAlert.text}
              </p>
            )}
          </Alert>
        )}

        <div className="mt-10 font-Korolev-Light text-sm italic">
          This site is protected by reCAPTCHA and the Google{' '}
          <a className="underline" target="blank" href="https://policies.google.com/privacy">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a className="underline" target="blank" href="https://policies.google.com/terms">
            Terms of Service
          </a>{' '}
          apply.
        </div>
      </form>
    </>
  );
};

export default ForgotPassword;
